import React from 'react';
import Expand from '@pushpay/expand';
import { CampaignsIcon, DashboardIcon, TransactionsIcon, ReconciliationIcon, FundsIcon, CommunityIcon, DonorDevelopmentIcon, RecurringIcon, GiftIcon, GivingStatementsIcon, LockIcon, ResourceIcon, PaymentRequestIcon, SandboxSettingsIcon, SettingsIcon, HelpCenterIcon, FinanceIcon, AppAnalyticsIcon } from '@pushpay/iconography';
import { useScrollIntoViewOnMount } from '@pushpay/utils';
import { isCurrentPage } from '../../helpers';
import LinkItem from './link-item';
import useStyles from './use-styles';

const itemUrlOrPath = ({
  url,
  routePath
}) => url || routePath || '';

const getChildrenUrls = list => list.map(item => itemUrlOrPath(item));

const itemIcons = {
  dashboard: className => /*#__PURE__*/React.createElement(DashboardIcon, {
    className: className,
    "data-pp-at-target": "DashboardIcon"
  }),
  transactions: className => /*#__PURE__*/React.createElement(TransactionsIcon, {
    className: className,
    "data-pp-at-target": "TransactionsIcon"
  }),
  reconciliation: className => /*#__PURE__*/React.createElement(ReconciliationIcon, {
    className: className,
    "data-pp-at-target": "ReconciliationIcon"
  }),
  finance: className => /*#__PURE__*/React.createElement(FinanceIcon, {
    className: className,
    "data-pp-at-target": "FinanceIcon"
  }),
  funds: className => /*#__PURE__*/React.createElement(FundsIcon, {
    className: className,
    "data-pp-at-target": "FundsIcon"
  }),
  campaigns: className => /*#__PURE__*/React.createElement(CampaignsIcon, {
    className: className,
    "data-pp-at-target": "CampaignsIcon"
  }),
  community: className => /*#__PURE__*/React.createElement(CommunityIcon, {
    className: className,
    "data-pp-at-target": "CommunityIcon"
  }),
  appanalytics: className => /*#__PURE__*/React.createElement(AppAnalyticsIcon, {
    className: className,
    "data-pp-at-target": "AppAnalyticsIcon"
  }),
  donordevelopment: className => /*#__PURE__*/React.createElement(DonorDevelopmentIcon, {
    className: className,
    "data-pp-at-target": "DonorDevelopmentIcon"
  }),
  recurring: className => /*#__PURE__*/React.createElement(RecurringIcon, {
    className: className,
    "data-pp-at-target": "RecurringIcon"
  }),
  giftentry: className => /*#__PURE__*/React.createElement(GiftIcon, {
    className: className,
    "data-pp-at-target": "GiftIcon"
  }),
  givingstatements: className => /*#__PURE__*/React.createElement(GivingStatementsIcon, {
    className: className,
    "data-pp-at-target": "GivingStatementsIcon"
  }),
  resourcecenter: className => /*#__PURE__*/React.createElement(ResourceIcon, {
    className: className,
    "data-pp-at-target": "ResourceIcon"
  }),
  settings: className => /*#__PURE__*/React.createElement(SettingsIcon, {
    className: className,
    "data-pp-at-target": "SettingsIcon"
  }),
  pushpayadmin: className => /*#__PURE__*/React.createElement(LockIcon, {
    className: className,
    "data-pp-at-target": "LockIcon"
  }),
  paymentrequests: className => /*#__PURE__*/React.createElement(PaymentRequestIcon, {
    className: className,
    "data-pp-at-target": "PaymentRequestsIcon"
  }),
  sandboxsettings: className => /*#__PURE__*/React.createElement(SandboxSettingsIcon, {
    className: className,
    "data-pp-at-target": "SandboxSettingsIcon"
  }),
  pushpayaccountssubmenu: className => /*#__PURE__*/React.createElement(CommunityIcon, {
    className: className,
    "data-pp-at-target": "PushpayaccountsIcon"
  }),
  helpcenter: className => /*#__PURE__*/React.createElement(HelpCenterIcon, {
    className: className,
    "data-pp-at-target": "HelpCenterIcon"
  })
}; // Targets in here are hardcoded for compliance with data set on Pendo
// in case they aren't hardcoded, they will comply to `displayName`

const givingStatementsTarget = 'Giving Statement';

function getHasSubItems({
  items
}) {
  return Array.isArray(items) && Boolean(items.length);
}

const LinkList = ({
  isSPA,
  items,
  onSelectItem,
  classes: classesProp,
  nav: navWrapper
}) => {
  const classes = useStyles(classesProp);
  const activeItem = useScrollIntoViewOnMount({
    container: navWrapper,
    behavior: 'auto'
  });
  return /*#__PURE__*/React.createElement("ul", {
    className: classes.linklist
  }, /*#__PURE__*/React.createElement(Expand.Group, null, items.map(item => {
    const lowerCaseKey = item.key.toLowerCase();
    const subItemsUrls = Array.isArray(item.items) && item.items.length > 0 ? getChildrenUrls(item.items) : [];
    return /*#__PURE__*/React.createElement(Expand.Item, {
      key: lowerCaseKey,
      "data-pp-at-target": `expand-${item.key.toLowerCase()}`,
      initialExpandStatus: isCurrentPage(itemUrlOrPath(item), subItemsUrls) ? 'preexpanded' : 'collapsed'
    }, (expandStatus, updateExpandStatus) => {
      var _item$render;

      const hasSubItems = getHasSubItems(item);
      const renderAsExpand = ['funds', 'giftentry', 'settings'].includes(lowerCaseKey);
      return /*#__PURE__*/React.createElement("li", {
        className: classes.topLevelItem,
        "data-pp-at-target": "top-level-nav-item"
      }, /*#__PURE__*/React.createElement(LinkItem, {
        renderAsExpand: renderAsExpand,
        isSPA: isSPA,
        routePath: item.routePath || '',
        url: item.url || '',
        "data-pp-at-target": // This one is hardcoded to comply with legacy Pendo configuration. Do not change this.
        lowerCaseKey === 'givingstatements' ? givingStatementsTarget : item.displayName,
        hasSubItems: hasSubItems,
        expandStatus: expandStatus,
        subItemsUrls: subItemsUrls,
        warning: item.warning,
        onSelect: () => {
          if (!item.routePath && !renderAsExpand) return;

          if (hasSubItems) {
            updateExpandStatus();
          } else if (item.routePath) {
            updateExpandStatus();
            onSelectItem();
          } else {
            onSelectItem();
          }
        }
      }, Boolean(itemIcons[lowerCaseKey]) && itemIcons[lowerCaseKey](classes.icon), (_item$render = item.render) !== null && _item$render !== void 0 ? _item$render : item.displayName), Array.isArray(item.items) && /*#__PURE__*/React.createElement(Expand.Panel, {
        expandStatus: expandStatus,
        "data-pp-at-target": `navigation-panel-${item.key.toLowerCase()}`
      }, /*#__PURE__*/React.createElement("ul", {
        className: classes.subList
      }, item.items.map(subItem => {
        var _subItem$render;

        const isActive = isCurrentPage(itemUrlOrPath(item));
        return /*#__PURE__*/React.createElement("li", {
          className: classes.subItem,
          key: subItem.key,
          ref: isActive ? activeItem : undefined
        }, /*#__PURE__*/React.createElement(LinkItem, {
          isSPA: isSPA,
          routePath: subItem.routePath || '',
          url: subItem.url || '',
          isSubItem: true,
          hasSubItems: getHasSubItems(subItem),
          "data-pp-at-target": subItem.displayName,
          warning: subItem.warning,
          onSelect: () => {
            if (!item.routePath) return;
            onSelectItem();
          }
        }, (_subItem$render = subItem.render) !== null && _subItem$render !== void 0 ? _subItem$render : subItem.displayName));
      }))));
    });
  })));
};

export default LinkList;